import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getLeagueConfig,
  getMindCricketApiPayload,
  isMindCricketApiSuccess,
  patchLeagueEconomy,
  patchLeagueLevel,
  putLeagueLevels,
} from '../../services/mindCricketLeagueApi';
import {
  cloneLevels,
  emptyEconomy,
  toIntOrNull,
  toNonNegInt,
  validateLevelsClient,
} from './mindCricketLeagueUtils';

const ECONOMY_FIELDS = [
  { key: 'match_entry_fe', label: 'Entry FE' },
  { key: 'match_win_fe', label: 'Win FE' },
  { key: 'match_tie_fe', label: 'Tie FE' },
  { key: 'match_lose_fe', label: 'Lose FE' },
];

const LEVEL_NUMBER_FIELDS = [
  { key: 'games_required', label: 'Games', nullable: false },
  { key: 'demotion_max', label: 'Demotion max', nullable: true },
  { key: 'stay_min', label: 'Stay min', nullable: false },
  { key: 'stay_max', label: 'Stay max', nullable: false },
  { key: 'promotion_min', label: 'Promo min', nullable: true },
  { key: 'trophy_min', label: 'Trophy min', nullable: false },
  { key: 'stay_fe', label: 'Stay FE', nullable: false },
  { key: 'promotion_fe', label: 'Promo FE', nullable: true },
  { key: 'trophy_fe', label: 'Trophy FE', nullable: false },
];

function LeagueControlsTab({ onSessionExpired, showSnackbar }) {
  const [loading, setLoading] = useState(true);
  const [savingEconomy, setSavingEconomy] = useState(false);
  const [savingLevels, setSavingLevels] = useState(false);
  const [savingLevel, setSavingLevel] = useState(null);
  const [error, setError] = useState('');
  const [economy, setEconomy] = useState(emptyEconomy());
  const [levels, setLevels] = useState([]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getLeagueConfig();
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        setError(res?.message || 'Failed to load league config.');
        return;
      }
      const data = getMindCricketApiPayload(res) || {};
      setEconomy({ ...emptyEconomy(), ...(data.economy || {}) });
      setLevels(cloneLevels(data.levels || []));
    } catch (err) {
      setError(err.message || 'Failed to load league config.');
    } finally {
      setLoading(false);
    }
  }, [onSessionExpired]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleEconomyChange = (key, value) => {
    setEconomy((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveEconomy = async () => {
    const body = {};
    ECONOMY_FIELDS.forEach(({ key }) => {
      body[key] = toNonNegInt(economy[key], 0);
    });
    setSavingEconomy(true);
    setError('');
    try {
      const res = await patchLeagueEconomy(body);
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        const msg = res?.message || 'Failed to save economy.';
        setError(msg);
        showSnackbar(msg, 'error');
        return;
      }
      const data = getMindCricketApiPayload(res);
      if (data?.economy) {
        setEconomy({ ...emptyEconomy(), ...data.economy });
      } else {
        setEconomy(body);
      }
      showSnackbar('Match economy saved. Applies to new matches only.', 'success');
    } catch (err) {
      const msg = err.message || 'Failed to save economy.';
      setError(msg);
      showSnackbar(msg, 'error');
    } finally {
      setSavingEconomy(false);
    }
  };

  const handleLevelFieldChange = (index, key, raw) => {
    setLevels((prev) => {
      const next = cloneLevels(prev);
      const row = { ...next[index] };
      if (key === 'band_name') {
        row.band_name = raw;
      } else {
        const meta = LEVEL_NUMBER_FIELDS.find((f) => f.key === key);
        if (meta?.nullable && (raw === '' || raw === null)) {
          row[key] = null;
        } else {
          row[key] = raw === '' ? '' : Number(raw);
        }
      }
      next[index] = row;
      return next;
    });
  };

  const normalizeLevelsForSave = () =>
    levels.map((row) => {
      const out = {
        level: Number(row.level),
        games_required: toNonNegInt(row.games_required, 10),
        band_name: String(row.band_name || ''),
        demotion_max: toIntOrNull(row.demotion_max),
        stay_min: toNonNegInt(row.stay_min, 0),
        stay_max: toNonNegInt(row.stay_max, 0),
        promotion_min: toIntOrNull(row.promotion_min),
        trophy_min: toNonNegInt(row.trophy_min, 0),
        stay_fe: toNonNegInt(row.stay_fe, 0),
        promotion_fe: toIntOrNull(row.promotion_fe),
        trophy_fe: toNonNegInt(row.trophy_fe, 0),
      };
      if (out.level === 1) out.demotion_max = null;
      if (out.level === 15) {
        out.promotion_min = null;
        out.promotion_fe = null;
      }
      return out;
    });

  const handleSaveAllLevels = async () => {
    const payload = normalizeLevelsForSave();
    const clientErr = validateLevelsClient(payload);
    if (clientErr) {
      setError(clientErr);
      showSnackbar(clientErr, 'error');
      return;
    }
    setSavingLevels(true);
    setError('');
    try {
      const res = await putLeagueLevels(payload);
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        const msg = res?.message || 'Failed to save levels.';
        setError(msg);
        showSnackbar(msg, 'error');
        return;
      }
      const data = getMindCricketApiPayload(res);
      if (Array.isArray(data?.levels)) {
        setLevels(cloneLevels(data.levels));
      } else {
        setLevels(cloneLevels(payload));
      }
      showSnackbar('All levels saved. Applies to new cycles only.', 'success');
    } catch (err) {
      const msg = err.message || 'Failed to save levels.';
      setError(msg);
      showSnackbar(msg, 'error');
    } finally {
      setSavingLevels(false);
    }
  };

  const handleSaveOneLevel = async (index) => {
    const payload = normalizeLevelsForSave()[index];
    if (!payload) return;
    setSavingLevel(payload.level);
    setError('');
    try {
      const { level, ...body } = payload;
      const res = await patchLeagueLevel(level, body);
      if (onSessionExpired(res)) return;
      if (!isMindCricketApiSuccess(res)) {
        const msg = res?.message || `Failed to save level ${level}.`;
        setError(msg);
        showSnackbar(msg, 'error');
        return;
      }
      const data = getMindCricketApiPayload(res);
      const updated = data?.level || data;
      if (updated && Number(updated.level) === level) {
        setLevels((prev) => {
          const next = cloneLevels(prev);
          next[index] = { ...next[index], ...updated };
          return next;
        });
      }
      showSnackbar(`Level ${level} saved. Applies to new cycles only.`, 'success');
    } catch (err) {
      const msg = err.message || 'Failed to save level.';
      setError(msg);
      showSnackbar(msg, 'error');
    } finally {
      setSavingLevel(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert severity="info">
        Changes apply to new cycles and new matches only. Open cycles keep their stored{' '}
        <code>games_required</code>; already-charged entry FE is not retroactively changed.
      </Alert>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Match economy</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<RefreshIcon />} onClick={loadConfig} disabled={savingEconomy}>
              Reload
            </Button>
            <Button
              variant="contained"
              startIcon={savingEconomy ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveEconomy}
              disabled={savingEconomy}
            >
              Save economy
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {ECONOMY_FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              label={label}
              type="number"
              value={economy[key] ?? ''}
              onChange={(e) => handleEconomyChange(key, e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 160 }}
            />
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Levels 1–15</Typography>
          <Button
            variant="contained"
            startIcon={savingLevels ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAllLevels}
            disabled={savingLevels || levels.length === 0}
          >
            Save all levels
          </Button>
        </Box>
        <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Lvl</TableCell>
                <TableCell>Band</TableCell>
                {LEVEL_NUMBER_FIELDS.map((f) => (
                  <TableCell key={f.key}>{f.label}</TableCell>
                ))}
                <TableCell align="right">Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {levels.map((row, index) => (
                <TableRow key={row.level}>
                  <TableCell>{row.level}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={row.band_name ?? ''}
                      onChange={(e) => handleLevelFieldChange(index, 'band_name', e.target.value)}
                      sx={{ minWidth: 140 }}
                    />
                  </TableCell>
                  {LEVEL_NUMBER_FIELDS.map((f) => {
                    const lockedNull =
                      (row.level === 1 && f.key === 'demotion_max') ||
                      (row.level === 15 && (f.key === 'promotion_min' || f.key === 'promotion_fe'));
                    return (
                      <TableCell key={f.key}>
                        <TextField
                          size="small"
                          type="number"
                          value={lockedNull ? '' : row[f.key] ?? ''}
                          disabled={lockedNull}
                          placeholder={lockedNull ? 'null' : ''}
                          onChange={(e) => handleLevelFieldChange(index, f.key, e.target.value)}
                          inputProps={{ min: 0, step: 1 }}
                          sx={{ width: 88 }}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSaveOneLevel(index)}
                      disabled={savingLevel === row.level || savingLevels}
                    >
                      {savingLevel === row.level ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default LeagueControlsTab;
